import {
  Box,
  Button,
  FormControl,
  MenuItem,
  Select,
  Typography,
  useTheme,
} from "@mui/material";
import useMediaQuery from "@mui/material/useMediaQuery";
import * as yup from "yup";

import DeleteOutlineOutlinedIcon from "@mui/icons-material/DeleteOutlineOutlined";
import { DataGrid } from "@mui/x-data-grid";
import { useFormik } from "formik";
import { useEffect, useState } from "react";
import ConfirmationDialog from "../../components/ConfirmationDialog";
import Header from "../../components/Header";
import ProjectPermissionApi from "../../services/api/ProjectPermission";
import ProjectApi from "../../services/api/projects";
import RoleApi from "../../services/api/roles";
import { tokens } from "../../theme";

const ProjectPermissions = () => {
  const isSmallScreen = useMediaQuery("(max-width: 767px)");
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const [isEdit, setIsEdit] = useState(false);
  const [projects, setProjects] = useState([]);
  const [roles, setRoles] = useState([]);
  const [roleId, setRoleId] = useState();
  const [roleProjects, setRoleProjects] = useState([]);

  const columns = [
    {
      field: "projectName",
      headerName: "Project",
      flex: 1,
      cellClassName: "name-column--cell",
    },
    {
      field: "",
      headerName: "Action",
      width: 200,
      renderCell: ({ row }) => {
        return (
          <Box display="flex">
            <DeleteOutlineOutlinedIcon
              fontSize="small"
              color="error"
              style={{ marginRight: "4px" }}
            />
            <Typography
              variant="button"
              color="error"
              onClick={() => destroy(row)}
              style={{ cursor: "pointer" }}
            >
              Delete
            </Typography>
          </Box>
        );
      },
    },
  ];

  useEffect(() => {
    fetchRoles();
    fetchProjects();
  }, []);

  const formik = useFormik({
    initialValues: initialValues,
    validationSchema: checkoutSchema,

    onSubmit: async (values, { resetForm }) => {
      createEntity(values);

      resetForm({ values: { ...initialValues, role_id: roleId } });
      handleAfterReset();
    },
  });

  const createEntity = async (values) => {
    const response = await ProjectPermissionApi().create(values);

    console.log(createdSuccessMsg, response);
    fetchRoleProjects(roleId);
  };

  const handleAfterReset = () => {
    setIsEdit(false);
  };

  const fetchRoles = async () => {
    try {
      const data = await RoleApi().getAll();

      setRoles(data.results);
    } catch (error) {
      console.log("Not loading roles", error);
    }
  };

  const fetchProjects = async () => {
    try {
      const data = await ProjectApi().getAll();

      setProjects(data.results);
    } catch (error) {
      console.log("Not loading projects", error);
    }
  };

  const handleRoleChange = (event) => {
    setRoleId(event.target.value);
    fetchRoleProjects(event.target.value);
    formik.handleChange(event);
  };

  const [isConfirmationOpen, setConfirmationOpen] = useState(false);
  const [selectedId, setSelectedId] = useState("");

  const destroy = (row) => {
    setSelectedId(row.id);
    handleOpenConfirmation();
  };

  const handleOpenConfirmation = () => {
    setConfirmationOpen(true);
  };

  const handleCloseConfirmation = () => {
    setConfirmationOpen(false);
  };

  const handleConfirmAction = () => {
    try {
      const response = ProjectPermissionApi().destroy(selectedId);

      console.log(deletedSuccessMsg, response);
      fetchRoleProjects(roleId);
    } catch (error) {
      console.log(deleteErrorMsg, error);
    }

    handleCloseConfirmation();
  };

  const fetchRoleProjects = async (roleId) => {
    try {
      const data = await ProjectPermissionApi().getByRoleId(roleId);

      setRoleProjects(data);
    } catch (error) {
      setRoleProjects([]);
      if (error.response.status === 404) {
        console.log("This role has no projects.");
      } else {
        console.log("Loading error", error.response);
      }
    }
  };

  return (
    <>
      <Box
        sx={{
          padding: "15px 30px",
          borderBottom: "1px solid var(--color-primary-200)",
        }}
      >
        <Header title={`${formName}`} />
      </Box>

      <Box m="30px" display="flex" flexDirection="column" flex={1}>
        <form onSubmit={formik.handleSubmit}>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              marginBottom: "15px",
              gap: 2,
            }}
          >
            <Box sx={{ flex: 1, display: "flex", gap: 2 }}>
              <FormControl fullWidth size="small">
                <Select
                  value={formik.values.role_id || ""}
                  onChange={handleRoleChange}
                  name="role_id"
                  displayEmpty
                  renderValue={(selected) => {
                    if (!selected) {
                      return (
                        <span className="placeholder-text">Select Role</span>
                      );
                    }
                    return roles?.find((r) => r.id === selected)?.name;
                  }}
                >
                  <MenuItem value="" disabled>
                    Select Role
                  </MenuItem>

                  {roles?.map((role) => (
                    <MenuItem key={role.id} value={role.id}>
                      {role.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              <FormControl fullWidth size="small">
                <Select
                  value={formik.values.project_id || ""}
                  onChange={formik.handleChange}
                  name="project_id"
                  displayEmpty
                  renderValue={(selected) => {
                    if (!selected) {
                      return (
                        <span className="placeholder-text">Select Project</span>
                      );
                    }
                    return projects?.find((p) => p.id === selected)?.name;
                  }}
                >
                  <MenuItem value="" disabled>
                    Select Project
                  </MenuItem>

                  {projects?.map((project) => (
                    <MenuItem key={project.id} value={project.id}>
                      {project.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>
            <Button
              type="submit"
              variant="contained"
              sx={{
                whiteSpace: "nowrap",
                flexShrink: 0,
              }}
            >
              {isEdit ? "Update" : "Save"}
            </Button>
          </Box>

          <Box height="75vh">
            {roleProjects && (
              <DataGrid
                rows={roleProjects}
                columns={columns}
                pageSize={10}
                rowsPerPageOptions={[10, 25, 50]}
                columnHeaderHeight={isSmallScreen ? 70 : 60}
                sx={{
                  "& .MuiDataGrid-columnHeader--moving": {
                    backgroundColor: "inherit !important",
                  },
                  ...(isSmallScreen && {
                    "& .MuiDataGrid-columnSeparator": {
                      display: "none",
                    },
                    "& .MuiDataGrid-sortIcon": {
                      display: "none",
                    },
                    "& .MuiDataGrid-menuIcon": {
                      display: "none",
                    },
                    "& .MuiDataGrid-columnHeader": {
                      padding: "4px !important",
                      justifyContent: "center",
                      alignItems: "center",
                    },
                    "& .MuiDataGrid-columnHeaderTitleContainer": {
                      padding: "0 !important",
                      margin: "0 !important",
                      width: "100%",
                      display: "flex",
                      justifyContent: "center",
                    },
                    "& .MuiDataGrid-columnHeaderTitle": {
                      whiteSpace: "normal !important",
                      wordBreak: "normal",
                      textAlign: "center",
                      lineHeight: 1.2,
                    },
                  }),
                }}
              />
            )}
          </Box>
        </form>
      </Box>
      <ConfirmationDialog
        open={isConfirmationOpen}
        onClose={handleCloseConfirmation}
        onConfirm={handleConfirmAction}
      />
    </>
  );
};

const checkoutSchema = yup.object().shape({
  role_id: yup.number().required("required"),
  project_id: yup.number().required("required"),
});

const initialValues = {
  role_id: "",
  project_id: "",
};

const formName = "Project Permission";
const createdSuccessMsg = `${formName} created:`;
const updatedSuccessMsg = `${formName} updated:`;
const deletedSuccessMsg = `${formName} deleted successfully: `;
const deleteErrorMsg = `Not deleted ${formName}`;

export default ProjectPermissions;
